import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { UserPlus } from "lucide-react";
import { api, ApiError } from "@/lib/api";

const searchSchema = z.object({
  token: z.string().min(1),
});

export const Route = createFileRoute("/accept-invite")({
  component: AcceptInvitePage,
  validateSearch: searchSchema,
});

interface InvitePreview {
  email: string;
  role: "admin" | "member";
  company: { id: string; name: string } | null;
  expiresAt: number;
}

function AcceptInvitePage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const previewQ = useQuery<InvitePreview>({
    queryKey: ["invite", "preview", token],
    queryFn: () => api.get(`/auth/invite/${token}`),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () =>
      api.post("/auth/accept-invite", { token, password, name: name.trim() }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate({ to: "/" });
    },
    onError: (err) => {
      const detail =
        err instanceof ApiError ? JSON.stringify(err.payload) : String(err);
      setError(detail);
    },
  });

  if (previewQ.isLoading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }
  if (previewQ.isError) {
    return (
      <div className="min-h-screen grid place-items-center p-4">
        <div className="card-elevated p-6 max-w-md text-center space-y-2">
          <h1 className="font-display font-semibold text-lg">Invite no válido</h1>
          <p className="text-sm text-base-content/70">
            Es posible que este link haya expirado o ya se haya usado. Pedile
            a tu contacto en Axorial que te genere uno nuevo.
          </p>
        </div>
      </div>
    );
  }

  const passwordOk = password.length >= 8 && password === confirm;

  return (
    <div className="min-h-screen grid place-items-center bg-base-200 p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          accept.mutate();
        }}
        className="card-elevated w-full max-w-sm p-6 space-y-4"
      >
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Axorial · Portal
          </div>
          <h1 className="text-2xl font-display font-semibold mt-1">Crear tu cuenta</h1>
          <p className="text-sm text-base-content/65 mt-1">
            Bienvenido(a) a <strong>{previewQ.data?.company?.name ?? "Axorial"}</strong>. Te
            invitaron como <strong>{previewQ.data?.email}</strong>.
          </p>
        </div>

        <label className="form-control w-full">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Nombre completo
          </span>
          <input
            type="text"
            className="input input-bordered w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
          />
        </label>
        <label className="form-control w-full">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Contraseña (mín. 8)
          </span>
          <input
            type="password"
            className="input input-bordered w-full"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
          />
        </label>
        <label className="form-control w-full">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Confirmar contraseña
          </span>
          <input
            type="password"
            className="input input-bordered w-full"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </label>

        {error && (
          <div className="text-sm text-error border border-error/30 bg-error/5 rounded-md p-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={accept.isPending || !name.trim() || !passwordOk}
          className="btn btn-primary w-full"
        >
          {accept.isPending && <span className="loading loading-spinner loading-xs" />}
          <UserPlus className="size-4" />
          Crear cuenta
        </button>
      </form>
    </div>
  );
}
